/**
 * UserAvatar - Reusable avatar component with image or colored initial fallback.
 *
 * Shows the user's uploaded avatar image if available, otherwise displays
 * a colored circle with the user's first initial. Color is deterministic
 * based on user ID for consistency across the app.
 */

import React, { useState } from 'react';

interface AvatarUser {
  id: number;
  full_name?: string;
  username?: string;
  avatar_url?: string | null;
}

interface UserAvatarProps {
  user: AvatarUser;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
}

const SIZES: Record<string, { px: string; text: string }> = {
  sm: { px: 'w-6 h-6', text: 'text-[10px]' },
  md: { px: 'w-8 h-8', text: 'text-sm' },
  lg: { px: 'w-12 h-12', text: 'text-lg' },
  xl: { px: 'w-24 h-24', text: 'text-3xl' },
};

const COLORS = [
  'bg-red-500',
  'bg-orange-500',
  'bg-amber-500',
  'bg-emerald-500',
  'bg-teal-500',
  'bg-cyan-500',
  'bg-blue-500',
  'bg-indigo-500',
  'bg-purple-500',
  'bg-pink-500',
];

const UserAvatar: React.FC<UserAvatarProps> = ({ user, size = 'md', className = '' }) => {
  const [imgError, setImgError] = useState(false);
  const sizeStyles = SIZES[size];
  const initial = (user.full_name || user.username || '?').charAt(0).toUpperCase();
  const colorClass = COLORS[user.id % COLORS.length];

  if (user.avatar_url && !imgError) {
    return (
      <img
        src={user.avatar_url}
        alt={user.full_name || user.username || 'Avatar'}
        onError={() => setImgError(true)}
        className={`${sizeStyles.px} rounded-full object-cover flex-shrink-0 ${className}`}
      />
    );
  }

  return (
    <div
      className={`${sizeStyles.px} ${colorClass} rounded-full flex items-center justify-center text-white font-medium flex-shrink-0 ${sizeStyles.text} ${className}`}
    >
      {initial}
    </div>
  );
};

export default UserAvatar;
